//all components in this directly will be exported from this file
export * from './puck';
export { default as SidePanel } from './sidePanel';
import {
  exportSynchTripCSV,
  exportSynchTripResults,
  getSynchrotronTripHeaderButtons,
  GetSynchrotronTripHeaderButtonsParams,
  getSynchTripStatus,
  mapSynchTripResultData,
} from './utils';
import Header from 'components/header/Header';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useSelectedAssignedCrystalIDs,
  useSelectedCrystalId,
  useSelectedTripResultIDs,
  useSynchTrip,
  useSynchTripEdit,
  useSynchTripSave,
} from './hooks';
import { SideBarOptions } from './synchrotronTabs';
import SynchrotronTripForm from './synchrotronTripForm';
import { SynchrotronTripTab } from './constants';
import SidePanel from './sidePanel';
import './synchrotronTrip.scss';
import UnassignedCrystals from './unassignedCrystals';
import CrystalDetails from 'components/crystal/crystalDetails';
import { useCollectedCrystals, useUnassignedCrystals } from 'components/crystal/hooks';
import { ModalDetails } from 'business/models/CustomModels';
import SynchTripReportOptions from './synchTripReportOptions';
import { RMModal } from 'components/modal';
import { IStyle } from 'Styles';
import SynchrotronTripResults from './tripResult/synchrotronTripResults';
import { useEditedSynchTrip, useTripEditStatus } from 'redux/hooks/synchrotron';
import ImportSynchTripResultWizard from './importSynchTripResult/wizard';
import { SynchTripExportDTO, SynchTripResultExportDTO } from 'business/models/Models';
import ImportSynchTripWizard from './importSynchTrip/importSynchTripWizard';
import { showAlertSnackBar } from 'utils/Global';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { KeyboardKeys } from 'constants/';
import ETreeNodeAccessRightNames from 'enums/ETreeNodeAccessRightNames';
import {
  useSaveDiscardButtons,
  UseSaveDiscardButtonsParams,
} from 'CustomHooks/useSaveDiscardButtons';

const SynchrotronTrip = () => {
  const { id: tripIdParams } = useParams();
  const tripId = parseInt(tripIdParams);
  const [activeTab, setActiveTab] = useState<number>(SynchrotronTripTab.Info);
  const [openImport, setOpenImport] = useState<boolean>(false);
  const { refetchSynchTrip } = useSynchTrip(tripId);
  const { collectedCrystals, refetchCollectedCrystals } = useCollectedCrystals(tripId);
  const { unassignedCrystals, refetchUnassignedCrystals } = useUnassignedCrystals();
  const selectedCrystalIds = useSelectedAssignedCrystalIDs();
  const editStatus = useTripEditStatus();
  const editedSynchTrip = useEditedSynchTrip();
  document.title = editedSynchTrip?.synchTripName;
  const {
    crystalsResultsEdit,
    unassignedCrystalsEdit,
    handleInputChange,
    handleResultInputChange,
    handleSave,
    handleDiscard,
    handleCollected,
    handleUnassignCrystal,
    handleAssignCrystals,
  } = useSynchTripEdit(unassignedCrystals, collectedCrystals);
  useSynchTripSave(
    crystalsResultsEdit,
    refetchSynchTrip,
    refetchUnassignedCrystals,
    refetchCollectedCrystals,
  );
  const [selectedCrystalId] = useSelectedCrystalId();
  const [openSynchTripResultImportWizard, setSynchTripResultImportWizard] =
    useState(false);
  const handleOk = () => {
    setRMModalDetails({ ...rmModalDetails, showModal: false });
  };

  const openSynchTripResultWizard = () => {
    setSynchTripResultImportWizard(true);
  };
  const isReadOnly = editedSynchTrip.accessRightName !== ETreeNodeAccessRightNames.Modify;

  const [rmModalDetails, setRMModalDetails] = useState<ModalDetails>({
    showModal: false,
    setShowModal: () => {},
    onOk: handleOk,
    body: '',
    heading: '',
    isOkOnly: true,
    okButtonText: 'OK',
    noButtonText: undefined,
    showButtons: false,
  });

  const selectedTripResults = useSelectedTripResultIDs();

  useEffect(() => {
    const openedWindows = JSON.parse(localStorage.getItem('openedNodesIds'));
    const openedWindowsList = !openedWindows ? [] : openedWindows;
    if (!openedWindows || !openedWindowsList.find((win) => win == tripId)) {
      openedWindowsList.push(tripId);
      localStorage.setItem('openedNodesIds', JSON.stringify(openedWindowsList));
    }

    window.addEventListener('beforeunload', beforeUnloadListener, {
      capture: true,
    });
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadListener);
    };
  }, [tripId]);

  const handleUserKeyPress = useCallback(
    (e: KeyboardEvent) => {
      const ctrlKey = e.ctrlKey || e.metaKey;

      if (ctrlKey && e.key === KeyboardKeys.S) {
        e.preventDefault();
        if (editStatus?.isDirty) {
          handleSave();
        }
      }
    },
    [handleSave, editStatus?.isDirty],
  );

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;
    window.addEventListener('keydown', handleUserKeyPress, { signal });

    return () => {
      abortController.abort();
    };
  }, [handleUserKeyPress]);

  const beforeUnloadListener = useCallback(
    (event) => {
      const openedWindows = JSON.parse(localStorage.getItem('openedNodesIds'));
      if (openedWindows && openedWindows.length) {
        const openedWindowsList = openedWindows.filter((win) => win != tripId);
        localStorage.setItem('openedNodesIds', JSON.stringify(openedWindowsList));
      }
    },
    [tripId],
  );

  const onTabClick = (tabId: number) => {
    setActiveTab(tabId);
  };

  const closeReportOptionsModal = () => {
    setRMModalDetails({ ...rmModalDetails, showModal: false });
  };

  const openCrystalReport = () => {
    setRMModalDetails(() => ({
      ...rmModalDetails,
      showModal: true,
      heading: 'Synchrotron Trip Report',
      body: (
        <SynchTripReportOptions
          synchTripId={tripId}
          closeReportOptionsModal={closeReportOptionsModal}
        />
      ),
    }));
  };

  const exportSynchTrip = (synchTripExportDto: SynchTripExportDTO) => {
    if (editStatus?.isDirty) {
      showAlertSnackBar(
        EAlertType.ERROR,
        ESnackBarPosition.TOP,
        `Please save your changes before export`,
      );
    } else {
      if (activeTab == SynchrotronTripTab.Info) {
        exportSynchTripCSV(synchTripExportDto, editedSynchTrip.synchTripName);
      } else if (activeTab == SynchrotronTripTab.CrystalResults) {
        const exportResults: SynchTripResultExportDTO[] = mapSynchTripResultData(
          collectedCrystals,
          selectedTripResults,
          {
            tripName: editedSynchTrip?.synchTripName,
            tripId: tripId,
          },
        );
        exportSynchTripResults(exportResults, editedSynchTrip?.synchTripName);
      }
    }
  };

  const openImportWizard = () => {
    setOpenImport(true);
  };

  const saveDiscardButtonsParams: UseSaveDiscardButtonsParams = {
    enableButtons: editStatus?.isDirty,
    onSave: handleSave,
    onDiscard: handleDiscard,
  };

  const saveDiscardButtons = useSaveDiscardButtons(saveDiscardButtonsParams);
  const synchrotronTripHeaderButtonsParams: GetSynchrotronTripHeaderButtonsParams = {
    isDisabled: activeTab === SynchrotronTripTab.UnassignedCrystals || isReadOnly,
    getSynchTripReport: openCrystalReport,
    importSynchTrip:
      activeTab === SynchrotronTripTab.Info
        ? openImportWizard
        : openSynchTripResultWizard,
    exportSynchTrip: () =>
      exportSynchTrip(
        new SynchTripExportDTO({
          synchTripId: tripId,
          crystalIds: selectedCrystalIds ?? [],
        }),
      ),
  };

  const synchrotronTripHeaderButtons = getSynchrotronTripHeaderButtons(
    synchrotronTripHeaderButtonsParams,
  );

  let renderedScreen: ReactElement[];
  if (activeTab == SynchrotronTripTab.Info) {
    renderedScreen = [
      <SynchrotronTripForm
        key={editedSynchTrip?.id}
        synchTrip={editedSynchTrip}
        handleInputChange={handleInputChange}
        handleCollected={handleCollected}
        unassignCrystal={handleUnassignCrystal}
        refetchSynchTrip={refetchSynchTrip}
      />,
      <SidePanel key={editedSynchTrip?.id} />,
    ];
  } else if (activeTab == SynchrotronTripTab.CrystalResults) {
    renderedScreen = [
      <SynchrotronTripResults
        key={editedSynchTrip?.id}
        crystals={crystalsResultsEdit}
        handleInputChange={handleResultInputChange}
        refetch={refetchCollectedCrystals}
        isReadOnly={isReadOnly}
      />,
      <CrystalDetails key={selectedCrystalId} crystalId={selectedCrystalId} />,
    ];
  } else if (activeTab == SynchrotronTripTab.UnassignedCrystals) {
    renderedScreen = [
      <UnassignedCrystals
        key={editedSynchTrip?.id}
        unassignedCrystals={unassignedCrystalsEdit}
        assignCrystals={handleAssignCrystals}
        synchTripStatus={getSynchTripStatus(editedSynchTrip?.status)}
        isReadOnly={isReadOnly}
      />,
      <CrystalDetails key={selectedCrystalId} crystalId={selectedCrystalId} />,
    ];
  }

  return (
    <>
      {openImport && (
        <ImportSynchTripWizard
          tripId={tripId}
          closeSynchTrip={() => setOpenImport(false)}
        />
      )}
      <Header
        headerButtons={synchrotronTripHeaderButtons}
        title={editedSynchTrip?.synchTripName}
        extendedActionSection={saveDiscardButtons}
        isNewTheme={true}
      />
      <div id="synchrotron-index-sidepanel-wrapper" className="flex flex-row h-[89.2%]">
        <div
          id="synchrotron-index-sidepanel-options"
          className="flex flex-col flex-none h-full bg-[#E0E0E0]"
        >
          <SideBarOptions onTabClick={onTabClick} activeTab={activeTab} />
        </div>
        <div
          id="synchrotron-index-sidepanel-renderedscreen-1"
          style={{ width: '74%' }}
          className="flex flex-col bg-light content-width p-1 pb-0 h-[100%]"
        >
          {renderedScreen[0]}
        </div>
        <div
          id="synchrotron-index-sidepanel-renderedscreen-2"
          style={styles.sidePanelRoot}
        >
          {renderedScreen[1]}
        </div>
      </div>
      <RMModal
        {...rmModalDetails}
        id="synchrotron-index-sidepanel-modal"
        setShowModal={handleOk}
        okayDisabled={false}
        dialogClassName="modal-xl trip_report_options_modal"
      />
      {openSynchTripResultImportWizard && (
        <ImportSynchTripResultWizard
          closeSynchTrip={() => setSynchTripResultImportWizard(false)}
          refetchCollectedCrystals={refetchCollectedCrystals}
        />
      )}
    </>
  );
};

const styles: IStyle = {
  sidePanelRoot: {
    display: 'block',
    width: '30%',
    height: '99.5%',
    top: '10.5%',
    left: '60%',
    right: 0,
    bottom: 0,
    borderLeft: '1px solid lightGray',
    backgroundColor: 'white',
  },
};
export default SynchrotronTrip;
